import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../shared/Modal';
import { piAdminService } from '../../services/piAdminService';
import { performanceService } from '../../services/performanceService';
import { submissionService } from '../../services/submissionService';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import { normalizeText } from '../../utils/normalizeInput';
import { formatPeriodLabel } from './periodFormat';
import { ExpenseItemsEditor } from './ExpenseItemsEditor';
import { ExpenseItemsTable } from './ExpenseItemsTable';
import { expenseTotal, expenseItemsError, toExpenseDbRows, fromExpenseDbRows } from './expenseItems';

// The enabled params come back as pi_system_parameters rows with the catalog
// embedded under `pi_parameters`. Read the catalog fields through this accessor
// so a different shape (flat catalog rows) only needs changing here.
const paramOf = (row) => row.pi_parameters || row;

export function PISubmissionForm({ submission, onSaved, onSubmitted, onClose }) {
  const readOnly = submission.status === 'submitted' || submission.status === 'approved';
  const [params, setParams] = useState([]);
  const [values, setValues] = useState({});
  const [netInclExp, setNetInclExp] = useState(false);
  const [note, setNote] = useState(submission.author_note || '');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [expenseItems, setExpenseItems] = useState([]);
  const [expenseDbRows, setExpenseDbRows] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [systemParams, existing, system, expenses] = await Promise.all([
          piAdminService.getSystemParameters(submission.pi_system_id),
          performanceService.getSubmissionPerformance(submission.id),
          piAdminService.getPISystem(submission.pi_system_id),
          performanceService.getExpenseItems(submission.id),
        ]);
        if (!active) return;
        setParams(systemParams);
        setNetInclExp(!!system?.net_includes_expenses);
        setExpenseDbRows(expenses || []);
        setExpenseItems(fromExpenseDbRows(expenses));
        const initial = {};
        existing.forEach((r) => { initial[r.pi_parameter_id] = String(r.value ?? ''); });
        setValues(initial);
      } catch (e) {
        if (active) setError(mapSupabaseError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [submission.id, submission.pi_system_id]);

  // code -> entered numeric value (NaN when blank) for derived net deposit.
  const numByCode = useMemo(() => {
    const map = {};
    params.forEach((row) => {
      const p = paramOf(row);
      map[p.code] = parseFloat(values[p.id]);
    });
    return map;
  }, [params, values]);

  const expensesParam = useMemo(
    () => params.map(paramOf).find((p) => p.code === 'expenses') || null,
    [params]
  );
  const numericParamRows = useMemo(
    () => params.filter((row) => paramOf(row).code !== 'expenses'),
    [params]
  );
  const expensesTotal = expenseTotal(expenseItems);

  // When expensesParam is set there is no single Expenses input, so
  // numByCode.expenses is NaN — the `expensesParam ? expensesTotal : …` branch
  // below short-circuits before that fallback is ever read.
  const netDeposit = (numByCode.deposits || 0) - (numByCode.withdrawals || 0)
    - (netInclExp ? (expensesParam ? expensesTotal : (numByCode.expenses || 0)) : 0);

  // input attrs per the catalog: pnl is signed (no min), ftd_count is integer
  // (step 1), everything else is a non-negative decimal.
  const inputAttrs = (p) => {
    if (p.code === 'pnl') return { step: 'any' };
    if (p.value_type === 'integer') return { min: '0', step: '1' };
    return { min: '0', step: 'any' };
  };

  const buildItems = () =>
    numericParamRows
      .map(paramOf)
      .filter((p) => values[p.id] !== undefined && values[p.id] !== '')
      .map((p) => ({ pi_parameter_id: p.id, value: parseFloat(values[p.id]) }));

  const persist = async () => {
    const items = buildItems();
    if (expensesParam) {
      // Non-transactional by design: items are saved first, then the mirrored
      // total. If saveSubmissionPerformance fails, the items survive and reload
      // on next open; the total is re-written on the next Save/Submit.
      await performanceService.saveExpenseItems(submission.id, submission.user_id, toExpenseDbRows(expenseItems));
      items.push({ pi_parameter_id: expensesParam.id, value: expensesTotal });
    }
    await performanceService.saveSubmissionPerformance(submission.id, submission.user_id, items);
    if (note !== (submission.author_note || '')) {
      await submissionService.updateDraft(submission.id, { author_note: normalizeText(note) });
    }
  };

  const handleSave = async () => {
    try { setBusy(true); setError(''); await persist(); toast.success('Draft saved'); onSaved(); }
    catch (e) { setError(mapSupabaseError(e)); } finally { setBusy(false); }
  };

  const handleSubmit = async () => {
    if (expensesParam) {
      const err = expenseItemsError(expenseItems);
      if (err) { setError(err); return; }
    }
    try { setBusy(true); setError(''); await persist(); await submissionService.submit(submission.id); toast.success('Submitted for review'); onSubmitted(); }
    catch (e) { setError(mapSupabaseError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} role="dialog" aria-modal="true" aria-label="Performance submission">
      <h3>Performance submission</h3>
      <p className="form-label">{formatPeriodLabel(submission.period, submission.period_start, submission.period_end)}</p>
      {submission.status === 'rejected' && submission.review_comment && (
        <div className="error-message" role="alert"><strong>Reviewer note:</strong> {submission.review_comment}</div>
      )}
      {error && <div className="error-message" role="alert">{error}</div>}

      {loading ? <p className="loading-msg">Loading…</p> : (
        <>
          <table className="kpi-bundle-table">
            <thead><tr><th>Parameter</th><th>Unit</th><th>Value</th></tr></thead>
            <tbody>
              {numericParamRows.map((row) => {
                const p = paramOf(row);
                return (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td>{p.unit}</td>
                    <td>
                      {readOnly
                        ? <span>{values[p.id] ?? '—'}</span>
                        : <input type="number" {...inputAttrs(p)} value={values[p.id] ?? ''}
                            onChange={(e) => setValues((v) => ({ ...v, [p.id]: e.target.value }))}
                            aria-label={`value for ${p.name}`} />}
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td><strong>Net deposit</strong></td>
                <td>USD</td>
                <td><span data-testid="net-deposit">{netDeposit}</span></td>
              </tr>
            </tbody>
          </table>

          {expensesParam && (
            readOnly
              ? <ExpenseItemsTable rows={expenseDbRows} />
              : <ExpenseItemsEditor items={expenseItems} onChange={setExpenseItems} />
          )}

          <div className="form-group">
            <label htmlFor="pi-sub-note">Notes (optional)</label>
            <textarea id="pi-sub-note" rows={3} maxLength={1000} value={note}
              onChange={(e) => setNote(e.target.value)} disabled={readOnly} />
          </div>

          <div className="modal-buttons">
            {!readOnly && (
              <>
                <button type="button" className="btn-secondary" onClick={handleSave} disabled={busy}>Save draft</button>
                <button type="button" className="btn-edit" onClick={handleSubmit} disabled={busy}>Submit</button>
              </>
            )}
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Close</button>
          </div>
        </>
      )}
    </Modal>
  );
}
